import { useEffect, useState } from "react";
import api from "@/lib/api";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/StatusBadge";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Cell,
} from "recharts";
import { fmtDate } from "@/lib/format";

const PERIODS = [
  { value: "monthly", label: "Son 1 Ay" },
  { value: "3m", label: "Son 3 Ay" },
  { value: "6m", label: "Son 6 Ay" },
  { value: "1y", label: "Son 1 Yıl" },
];

const COLORS = ["#0B3676", "#16A34A", "#F59E0B", "#DC2626", "#8B5CF6",
  "#06B6D4", "#EC4899", "#84CC16", "#F97316", "#14B8A6"];

const ROLE_LABELS = {
  admin: "Süper Admin", sales: "Satış Personeli",
  accounting: "Muhasebe", warehouse: "Depo", viewer: "Görüntüleme",
};

export default function SalesCompaniesReport() {
  const [period, setPeriod] = useState("monthly");
  const [data, setData] = useState(null);

  const load = async () => {
    const { data } = await api.get("/reports/sales-companies", { params: { period } });
    setData(data);
  };
  useEffect(() => { load(); }, [period]);

  const rows = data?.rows || [];
  const chartData = rows.filter((r) => r.count > 0);

  return (
    <div className="space-y-4">
      <div className="azd-card p-5 flex flex-wrap items-end gap-4 justify-between">
        <div>
          <label className="block text-xs font-semibold mb-1.5">Dönem</label>
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="min-w-[220px]" data-testid="period-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PERIODS.map((p) => (
                <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {data?.from_date && (
            <div className="text-xs text-[#6B7280] mt-1.5">
              {fmtDate(data.from_date)} tarihinden itibaren
            </div>
          )}
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-wider text-[#6B7280] font-bold">
            Bu Dönemde Eklenen Toplam Firma
          </div>
          <div className="text-3xl font-extrabold text-[#0B3676]" data-testid="total-count">
            {data?.total ?? 0}
          </div>
        </div>
      </div>

      <div className="azd-card p-5">
        <div className="text-sm font-bold text-[#08285A] mb-3 uppercase tracking-wider">
          Satış Personeline Göre Firma Eklemeleri
        </div>
        <div className="h-80">
          {chartData.length === 0 ? (
            <div className="h-full flex items-center justify-center text-[#6B7280] text-sm">
              Bu dönemde henüz firma eklenmemiş
            </div>
          ) : (
            <ResponsiveContainer>
              <BarChart data={chartData} layout="vertical" margin={{ left: 40 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                <YAxis type="category" dataKey="user_name" tick={{ fontSize: 11 }} width={160} />
                <Tooltip formatter={(v) => `${v} firma`} />
                <Bar dataKey="count" radius={[0, 6, 6, 0]}>
                  {chartData.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="azd-card overflow-hidden">
        <div className="overflow-x-auto azd-scrollbar">
          <table className="w-full text-sm">
            <thead className="bg-[#F6F8FB] text-[11px] uppercase tracking-wider text-[#6B7280]">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Personel</th>
                <th className="text-left px-4 py-3 font-semibold">Rol</th>
                <th className="text-right px-4 py-3 font-semibold">Bu Dönem</th>
                <th className="text-right px-4 py-3 font-semibold">Tüm Zamanlar</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={4} className="p-8 text-center text-[#6B7280]">Kayıt yok</td></tr>
              ) : rows.map((r) => (
                <tr key={r.user_id} className="border-t border-[#E5E7EB] hover:bg-[#F6F8FB]/50"
                    data-testid={`row-${r.user_id}`}>
                  <td className="px-4 py-3 font-semibold text-[#111827]">{r.user_name}</td>
                  <td className="px-4 py-3">
                    <Badge variant="info">{ROLE_LABELS[r.user_role] || r.user_role}</Badge>
                  </td>
                  <td className="px-4 py-3 text-right font-extrabold text-[#0B3676]">
                    {r.count}
                  </td>
                  <td className="px-4 py-3 text-right text-[#6B7280]">
                    {r.all_time_count}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
